import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { auth } from "../firebase";
import {
  BackendLoginRequest,
  TalentUserDTO,
  CompanyUserDTO,
  BackofficeUserDTO,
  AccountType,
} from "../types/firebase";
import { API_URL } from "../config/api";

const BACKEND_API_URL = API_URL;

// User Types
export type TalentUser = TalentUserDTO & {
  accountType: "talent";
};

export type CompanyUser = CompanyUserDTO & {
  accountType: "company";
  companyName: string;
  companyWebsite: string;
  fullname?: string; // User's actual full name (separate from company name)
  avatarId?: string; // User's avatar ID
  // Backend API format support (from /company/getcompany endpoint)
  id?: string;
  name?: string;
  website?: string;
  legalName?: string;
  location?: {
    country?: string;
    state?: string;
    city?: string;
    address?: string;
  };
  sector?: string;
  taxNumber?: string;
  taxOffice?: string;
  postalCode?: string;
  authorizedSignatoryName?: string;
  authorizedSignatorySoftcopyId?: string;
  companyType?: string;
  companySize?: string;
  culture?: string | null;
  users?: string[];
  groups?: string[];
};

export type BackofficeUser = BackofficeUserDTO & {
  accountType: "backoffice";
  fullname: string;
};

export type AppUser = TalentUser | CompanyUser | BackofficeUser;

// Legacy User Type (for backward compatibility)
export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  referenceCode: number;
  positionName: string;
  companyName: string;
  companyLogo: string;
  companyWebsite: string;
  companyLegalName: string;
  companyAddress: string;
  companyCity: string;
  companyCountry: string;
  taxNumber: number;
  taxOffice: string;
  authorizedSignatoryName: string;
  authorizedSignatoryIdCardSoftCopy?: string;
  companySize: string[];
  companySector: string;
  createdAt: string;
  password: string;
  avatar: string;
  workEmail: string;
  position: string;
};

interface AuthState {
  loading: boolean;
  error: string | null;
  user: AppUser | null;
  token: string | null;
  isAuthenticated: boolean;
}

export interface ProfileFormInputs {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
  workEmail: string;
}

// Helper Functions for User Data (to handle different account types)
export const getUserDisplayName = (user: AppUser | null): string => {
  if (!user) return "";

  if (user.accountType === "talent") {
    const talentUser = user as TalentUser & { accountType: "talent" };
    return `${talentUser.name || ""} ${talentUser.surname || ""}`.trim();
  }

  if (user.accountType === "company") {
    const companyUser = user as CompanyUser & { accountType: "company" };
    // For profile display, use fullname (user's actual name) instead of company name
    return companyUser.fullname || `${companyUser.name || ""} ${companyUser.surname || ""}`.trim();
  }

  if (user.accountType === "backoffice") {
    const backofficeUser = user as BackofficeUser & { accountType: "backoffice" };
    return backofficeUser.fullname || "";
  }

  return "";
};

export const getUserAvatar = (user: AppUser | null): string => {
  // Return empty string - avatar should be fetched with auth token
  // The component will handle fetching the avatar
  return "";
};

export const getUserId = (user: AppUser | null): string => {
  if (!user) return "";
  return user.userId || "";
};

export const getUserEmail = (user: AppUser | null): string => {
  if (!user) return "";
  return user.email || "";
};

export const getUserCompanyName = (user: AppUser | null): string => {
  if (!user || user.accountType !== "company") return "";
  const companyUser = user as CompanyUser & { accountType: "company" };
  return companyUser.companyName || "";
};

export const getUserCompanyWebsite = (user: AppUser | null): string => {
  if (!user || user.accountType !== "company") return "";
  const companyUser = user as CompanyUser & { accountType: "company" };
  return companyUser.companyWebsite || "";
};

export interface CompanyFormInputs {
  companyName: string;
  companyWebsite: string;
  companyLegalName: string;
  companyAddress: string;
  taxNumber: string;
  taxOffice: string;
  companyCity: string;
  companyCountry: string;
  companySector: string;
  companyType: string;
  companySize: string;
  postalCode: string;
  authorizedSignatoryName: string;
  authorizedSignatoryIdCardSoftCopy?: File | string;
  companyLogo?: File | string;
}

// Talent Signup Data
export interface TalentSignupData {
  referenceCode: string; // ⚠️ MUST be string, not number!
  fullname: string;
  email: string;
  countryCode: string;
  countryName: string;
  phone: string;
  location: string;
  password: string;
  isSocial: boolean;
  deviceId: string;
}

// Company Signup Data
export interface CompanySignupData {
  fullname: string;
  email: string;
  password: string;
  companyName: string;
  companyWebsite: string;
}

const initialState: AuthState = {
  loading: false,
  error: null,
  user: null,
  token: null,
  isAuthenticated: false,
};

const persistToken = (token: string | null) => {
  if (token) {
    localStorage.setItem("token", token);
    return;
  }

  localStorage.removeItem("token");
};

const clearStoredAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("accountType");
  localStorage.removeItem("user");
};

// Helper: Login with Firebase and Backend
const loginWithFirebaseAndBackend = async (
  email: string,
  password: string,
  accountType: AccountType
) => {
  // Step 1: Firebase Authentication
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const firebaseToken = await userCredential.user.getIdToken();
  const firebaseUid = userCredential.user.uid;

  // Step 2: Backend Login
  const endpoint = `${BACKEND_API_URL}/${accountType}/auth/login`;
  const loginRequest: BackendLoginRequest = {
    token: firebaseToken,
    uid: firebaseUid,
  };

  const response = await axios.post(endpoint, loginRequest);

  return {
    firebaseToken,
    backendUser: response.data,
  };
};

// Login Action
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (
    credentials: {
      email: string;
      password: string;
      accountType: AccountType;
    },
    { rejectWithValue }
  ) => {
    try {
      const { email, password, accountType } = credentials;

      const result = await loginWithFirebaseAndBackend(email, password, accountType);

      // Store token and user data (without password!)
      persistToken(result.firebaseToken);
      localStorage.setItem("accountType", accountType);
      localStorage.setItem("user", JSON.stringify(result.backendUser));

      return {
        token: result.firebaseToken,
        user: result.backendUser,
        accountType,
      };
    } catch (error: any) {
      console.error("Login error:", error);
      if (error.code) {
        // Firebase error
        return rejectWithValue(getFirebaseErrorMessage(error.code));
      }
      // Backend error
      return rejectWithValue(
        error.response?.data?.message || error.message || "Login failed"
      );
    }
  }
);

// Talent Signup Action
export const signUpTalent = createAsyncThunk(
  "auth/signUpTalent",
  async (userData: TalentSignupData, { rejectWithValue }) => {
    try {
      // Step 1: Firebase Create User
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );
      const firebaseToken = await userCredential.user.getIdToken();
      const firebaseUid = userCredential.user.uid;

      // Step 2: Backend Signup
      const endpoint = `${BACKEND_API_URL}/talent/auth/signup`;
      const response = await axios.post(endpoint, {
        ...userData,
        firebaseUid,
      });

      // Keep signup as a pre-auth step until email verification is completed
      await firebaseSignOut(auth);

      return {
        token: firebaseToken,
        user: response.data,
        accountType: "talent" as AccountType,
      };
    } catch (error: any) {
      console.error("Talent signup error:", error);
      if (error.code) {
        return rejectWithValue(getFirebaseErrorMessage(error.code));
      }
      return rejectWithValue(
        error.response?.data?.message || error.message || "Signup failed"
      );
    }
  }
);

// Company Signup Action
export const signUpCompany = createAsyncThunk(
  "auth/signUpCompany",
  async (userData: CompanySignupData, { rejectWithValue }) => {
    try {
      // Step 1: Firebase Create User
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );
      const firebaseToken = await userCredential.user.getIdToken();
      const firebaseUid = userCredential.user.uid;

      // Step 2: Backend Signup
      const signupEndpoint = `${BACKEND_API_URL}/company/auth/signup`;
      await axios.post(signupEndpoint, userData);

      // Step 3: Backend Login to fetch user profile
      const loginEndpoint = `${BACKEND_API_URL}/company/auth/login`;
      const loginResponse = await axios.post(loginEndpoint, {
        token: firebaseToken,
        uid: firebaseUid,
      });

      // Keep signup as a pre-auth step until email verification is completed
      await firebaseSignOut(auth);

      return {
        token: firebaseToken,
        user: loginResponse.data,
        accountType: "company" as AccountType,
      };
    } catch (error: any) {
      console.error("Company signup error:", error);
      if (error.code) {
        return rejectWithValue(getFirebaseErrorMessage(error.code));
      }
      return rejectWithValue(
        error.response?.data?.message || error.message || "Signup failed"
      );
    }
  }
);

// Hydrate User from localStorage
export const hydrateUser = createAsyncThunk(
  "auth/hydrateUser",
  async () => {
    let token = localStorage.getItem("token");
    const accountType = localStorage.getItem("accountType") as AccountType | null;
    const storedUser = localStorage.getItem("user");

    if (accountType && storedUser && auth.currentUser) {
      try {
        token = await auth.currentUser.getIdToken();
        persistToken(token);
      } catch (error) {
        console.error("Failed to refresh token during hydration:", error);
      }
    }

    if (token && accountType && storedUser) {
      return {
        token,
        accountType,
        user: JSON.parse(storedUser),
      };
    }
    return null;
  }
);

// Logout Action
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      await firebaseSignOut(auth);
      clearStoredAuth();
      return true;
    } catch (error: any) {
      console.error("Logout error:", error);
      // Clear localStorage even if Firebase logout fails
      clearStoredAuth();
      return rejectWithValue(error.message || "Logout failed");
    }
  }
);

// Helper: Firebase Error Messages
const getFirebaseErrorMessage = (code: string): string => {
  const errorMessages: { [key: string]: string } = {
    "auth/invalid-email": "Invalid email address",
    "auth/user-disabled": "This account has been disabled",
    "auth/user-not-found": "No account found with this email",
    "auth/wrong-password": "Incorrect password",
    "auth/email-already-in-use": "An account with this email already exists",
    "auth/weak-password": "Password is too weak",
    "auth/invalid-credential": "Invalid email or password",
    "auth/too-many-requests": "Too many attempts. Please try again later",
  };
  return errorMessages[code] || "An error occurred. Please try again.";
};

// Auth Slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    syncToken: (state, action: PayloadAction<string | null>) => {
      state.token = action.payload;
      persistToken(action.payload);
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      clearStoredAuth();
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        const user = { ...action.payload.user, accountType: action.payload.accountType } as AppUser;
        // For company users, preserve fullname from name + surname before it gets overwritten
        if (user.accountType === "company") {
          const companyUser = user as CompanyUser;
          companyUser.fullname = `${companyUser.name || ""} ${companyUser.surname || ""}`.trim();
        }
        state.user = user;
        state.isAuthenticated = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      })
      // Talent Signup
      .addCase(signUpTalent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signUpTalent.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
      })
      .addCase(signUpTalent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      })
      // Company Signup
      .addCase(signUpCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signUpCompany.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
      })
      .addCase(signUpCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      })
      // Hydrate User
      .addCase(hydrateUser.fulfilled, (state, action) => {
        if (action.payload) {
          state.token = action.payload.token;
          const user = { ...action.payload.user, accountType: action.payload.accountType } as AppUser;
          // For company users, ensure fullname is set from name + surname if not already set
          if (user.accountType === "company") {
            const companyUser = user as CompanyUser;
            if (!companyUser.fullname) {
              companyUser.fullname = `${companyUser.name || ""} ${companyUser.surname || ""}`.trim();
            }
          }
          state.user = user;
          state.isAuthenticated = true;
        }
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        // Clear state even if Firebase logout fails
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })
      // ============= TALENT AUTH EXTENSIONS =============
      .addCase(forgotPasswordTalent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPasswordTalent.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgotPasswordTalent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(sendVerificationEmailTalent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendVerificationEmailTalent.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendVerificationEmailTalent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyEmailTalent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmailTalent.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(verifyEmailTalent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // ============= COMPANY AUTH EXTENSIONS =============
      .addCase(signUpCompanyWithInvitation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signUpCompanyWithInvitation.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        const user = { ...action.payload.user, accountType: action.payload.accountType } as AppUser;
        if (user.accountType === "company") {
          const companyUser = user as CompanyUser;
          companyUser.fullname = `${companyUser.name || ""} ${companyUser.surname || ""}`.trim();
        }
        state.user = user;
        state.isAuthenticated = true;
      })
      .addCase(signUpCompanyWithInvitation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      })
      .addCase(forgotPasswordCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPasswordCompany.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgotPasswordCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(sendVerificationEmailCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendVerificationEmailCompany.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendVerificationEmailCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyEmailCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmailCompany.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(verifyEmailCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createInvitation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInvitation.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createInvitation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(checkUserEmailCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkUserEmailCompany.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(checkUserEmailCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // ============= BACKOFFICE AUTH EXTENSIONS =============
      .addCase(forgotPasswordBackoffice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPasswordBackoffice.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgotPasswordBackoffice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(sendVerificationEmailBackoffice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendVerificationEmailBackoffice.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendVerificationEmailBackoffice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyEmailBackoffice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmailBackoffice.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(verifyEmailBackoffice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(checkUserEmailBackoffice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkUserEmailBackoffice.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(checkUserEmailBackoffice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // ============= COMPANY FETCH & UPDATE =============
      .addCase(fetchCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompany.fulfilled, (state, action) => {
        state.loading = false;
        // Merge fetched data into existing user, but preserve fullname
        if (state.user && state.user.accountType === "company") {
          const companyUser = state.user as CompanyUser;
          const preservedFullname = companyUser.fullname;
          state.user = { ...state.user, ...action.payload };
          // Restore fullname to prevent company name from overwriting user's actual name
          if (preservedFullname) {
            (state.user as CompanyUser).fullname = preservedFullname;
          }
        }
      })
      .addCase(fetchCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateCompanyCulture.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCompanyCulture.fulfilled, (state, action) => {
        state.loading = false;
        // Merge updated data into existing user, but preserve fullname
        if (state.user && state.user.accountType === "company") {
          const companyUser = state.user as CompanyUser;
          const preservedFullname = companyUser.fullname;
          state.user = { ...state.user, ...action.payload };
          // Restore fullname to prevent company name from overwriting user's actual name
          if (preservedFullname) {
            (state.user as CompanyUser).fullname = preservedFullname;
          }
        }
        // Update localStorage
        localStorage.setItem("user", JSON.stringify(state.user));
      })
      .addCase(updateCompanyCulture.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.loading = false;
        // Merge updated data into existing user, but preserve fullname
        if (state.user && state.user.accountType === "company") {
          const companyUser = state.user as CompanyUser;
          const preservedFullname = companyUser.fullname;
          state.user = { ...state.user, ...action.payload };
          // Restore fullname to prevent company name from overwriting user's actual name
          if (preservedFullname) {
            (state.user as CompanyUser).fullname = preservedFullname;
          }
        }
        // Update localStorage
        localStorage.setItem("user", JSON.stringify(state.user));
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (data: ProfileFormInputs, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: AuthState };
      const currentUser = state.auth.user;

      if (!currentUser) {
        return rejectWithValue("No authenticated user found.");
      }

      const nextEmail = (data.email || data.workEmail || "").trim();
      const nextFirstName = (data.firstName || "").trim();
      const nextLastName = (data.lastName || "").trim();
      const nextFullName = `${nextFirstName} ${nextLastName}`.trim();

      const updatedUser: AppUser = {
        ...currentUser,
        email: nextEmail,
      };

      if ("name" in updatedUser) {
        (updatedUser as any).name = nextFirstName;
      }

      if ("surname" in updatedUser) {
        (updatedUser as any).surname = nextLastName;
      }

      if (updatedUser.accountType === "company") {
        const companyUser = updatedUser as CompanyUser;
        companyUser.fullname = nextFullName;
      }

      if (updatedUser.accountType === "backoffice") {
        const backofficeUser = updatedUser as BackofficeUser;
        backofficeUser.fullname = nextFullName;
      }

      localStorage.setItem("user", JSON.stringify(updatedUser));

      return updatedUser;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to update profile.");
    }
  }
);

// Placeholder Update Actions (Not implemented yet - Backend endpoints needed)
export const updatePassword = createAsyncThunk(
  "auth/updatePassword",
  async (_, { rejectWithValue }) => {
    return rejectWithValue("Password update not implemented yet. Firebase + Backend sync needed.");
  }
);

export const updateAvatar = createAsyncThunk(
  "auth/updateAvatar",
  async (_, { rejectWithValue }) => {
    return rejectWithValue("Avatar upload not implemented yet. File upload endpoint needed.");
  }
);

export const fetchCompany = createAsyncThunk(
  "auth/fetchCompany",
  async (companyId: string, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: AuthState };
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("No authentication token found");
      }

      const response = await axios.get(
        `${BACKEND_API_URL}/company/getcompany/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Return raw backend data - let component handle mapping
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch company"
      );
    }
  }
);

export const updateCompanyCulture = createAsyncThunk(
  "auth/updateCompanyCulture",
  async (data: { companyId: string; culture: any }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: AuthState };
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("No authentication token found");
      }

      const { companyId, culture } = data;

      await axios.put(
        `${BACKEND_API_URL}/company/updatecompanyculture`,
        { companyId, culture },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Fetch updated company data to get the latest culture info
      const response = await axios.get(
        `${BACKEND_API_URL}/company/getcompany/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update company culture"
      );
    }
  }
);

export const updateCompany = createAsyncThunk(
  "auth/updateCompany",
  async (data: { id: string } & CompanyFormInputs, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: AuthState };
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("No authentication token found");
      }

      const { id, ...companyData } = data;

      let logoUrl = "";
      let idCardId = "";

      // Upload logo if file is provided
      if (companyData.companyLogo instanceof File) {
        const logoFormData = new FormData();
        logoFormData.append("logo", companyData.companyLogo);
        logoFormData.append("companyId", id);

        try {
          const logoResponse = await axios.post(
            `${BACKEND_API_URL}/company/uploadcompanylogo`,
            logoFormData,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          logoUrl = logoResponse.data.logoId || "";
        } catch (error) {
          console.error("Failed to upload logo:", error);
        }
      } else if (typeof companyData.companyLogo === "string") {
        logoUrl = companyData.companyLogo;
      }

      // Upload ID card if file is provided
      if (companyData.authorizedSignatoryIdCardSoftCopy instanceof File) {
        const idCardFormData = new FormData();
        idCardFormData.append("idCard", companyData.authorizedSignatoryIdCardSoftCopy);
        idCardFormData.append("companyId", id);

        try {
          const idCardResponse = await axios.post(
            `${BACKEND_API_URL}/company/uploadidcard`,
            idCardFormData,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          idCardId = idCardResponse.data.idCardId || "";
        } catch (error) {
          console.error("Failed to upload ID card:", error);
        }
      } else if (typeof companyData.authorizedSignatoryIdCardSoftCopy === "string") {
        idCardId = companyData.authorizedSignatoryIdCardSoftCopy;
      }

      // Map frontend form data to backend request format
      const updateRequest = {
        CompanyId: id,
        Name: companyData.companyName,
        Website: companyData.companyWebsite,
        LegalName: companyData.companyLegalName,
        Location: {
          Country: companyData.companyCountry,
          City: companyData.companyCity,
          Address: companyData.companyAddress,
          State: "",
        },
        CompanyType: companyData.companyType,
        CompanySize: companyData.companySize,
        Sector: companyData.companySector,
        TaxNumber: companyData.taxNumber,
        TaxOffice: companyData.taxOffice,
        PostalCode: (companyData.postalCode || "").replace(/\D/g, ""),
        AuthorizedSignatoryName: companyData.authorizedSignatoryName,
        AuthorizedSignatorySoftcopyId: idCardId,
        CompanyLogo: logoUrl,
      };

      await axios.put(
        `${BACKEND_API_URL}/company/updatecompanyinfo`,
        updateRequest,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Fetch updated company data from backend to get the latest values
      const response = await axios.get(
        `${BACKEND_API_URL}/company/getcompany/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Return raw backend data - let component handle mapping
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update company"
      );
    }
  }
);

// ============= TALENT AUTH EXTENSIONS =============

// Forgot Password - Talent
export const forgotPasswordTalent = createAsyncThunk(
  "auth/forgotPasswordTalent",
  async (email: string, { rejectWithValue }) => {
    try {
      await axios.post(`${BACKEND_API_URL}/talent/auth/forgotPassword`, {
        email,
      });
      return { email };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send password reset email"
      );
    }
  }
);

// Send Verification Email - Talent
export const sendVerificationEmailTalent = createAsyncThunk(
  "auth/sendVerificationEmailTalent",
  async (user: TalentUserDTO, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BACKEND_API_URL}/talent/auth/sendVerificationEmail`, {
        user,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send verification email"
      );
    }
  }
);

// Verify Email - Talent
export const verifyEmailTalent = createAsyncThunk(
  "auth/verifyEmailTalent",
  async (
    data: {
      syncCode: string;
      verificationCode: string;
      email: string;
      userId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        `${BACKEND_API_URL}/talent/auth/verifyEmail`,
        data
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to verify email"
      );
    }
  }
);

// ============= COMPANY AUTH EXTENSIONS =============

// Signup with Invitation - Company
export const signUpCompanyWithInvitation = createAsyncThunk(
  "auth/signUpCompanyWithInvitation",
  async (
    data: {
      fullname: string;
      email: string;
      password: string;
      invitationCode: string;
    },
    { rejectWithValue }
  ) => {
    try {
      // Step 1: Firebase create user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );
      const firebaseToken = await userCredential.user.getIdToken();
      const firebaseUid = userCredential.user.uid;

      // Step 2: Backend signup with invitation
      await axios.post(
        `${BACKEND_API_URL}/company/auth/signupWithInvitation`,
        {
          fullname: data.fullname,
          email: data.email,
          password: data.password,
          invitationCode: data.invitationCode,
        }
      );

      // Step 3: Backend login to fetch user profile
      const loginResponse = await axios.post(
        `${BACKEND_API_URL}/company/auth/login`,
        {
          token: firebaseToken,
          uid: firebaseUid,
        }
      );

      // Store user data
      persistToken(firebaseToken);
      localStorage.setItem("accountType", "company");
      localStorage.setItem("user", JSON.stringify(loginResponse.data));

      return {
        token: firebaseToken,
        user: loginResponse.data,
        accountType: "company" as AccountType,
      };
    } catch (error: any) {
      // Firebase error handling
      if (error.code === "auth/email-already-in-use") {
        return rejectWithValue("Email is already in use");
      }
      if (error.code === "auth/weak-password") {
        return rejectWithValue("Password is too weak");
      }
      if (error.code === "auth/invalid-email") {
        return rejectWithValue("Invalid email address");
      }
      return rejectWithValue(
        error.response?.data?.message || error.message || "Signup failed"
      );
    }
  }
);

// Forgot Password - Company
export const forgotPasswordCompany = createAsyncThunk(
  "auth/forgotPasswordCompany",
  async (email: string, { rejectWithValue }) => {
    try {
      await axios.post(`${BACKEND_API_URL}/company/auth/forgotPassword`, {
        email,
      });
      return { email };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send password reset email"
      );
    }
  }
);

// Send Verification Email - Company
export const sendVerificationEmailCompany = createAsyncThunk(
  "auth/sendVerificationEmailCompany",
  async (user: CompanyUserDTO, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BACKEND_API_URL}/company/auth/sendVerificationEmail`, {
        user,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send verification email"
      );
    }
  }
);

// Verify Email - Company
export const verifyEmailCompany = createAsyncThunk(
  "auth/verifyEmailCompany",
  async (
    data: {
      syncCode: string;
      verificationCode: string;
      email: string;
      userId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        `${BACKEND_API_URL}/company/auth/verifyEmail`,
        data
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to verify email"
      );
    }
  }
);

// Create Invitation - Company
export const createInvitation = createAsyncThunk(
  "auth/createInvitation",
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BACKEND_API_URL}/company/auth/createInvitation`,
        { email }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create invitation"
      );
    }
  }
);

// Check User Email - Company
export const checkUserEmailCompany = createAsyncThunk(
  "auth/checkUserEmailCompany",
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BACKEND_API_URL}/company/auth/checkUserEmail/${encodeURIComponent(email)}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Email check failed"
      );
    }
  }
);

// ============= BACKOFFICE AUTH EXTENSIONS =============

// Forgot Password - Backoffice
export const forgotPasswordBackoffice = createAsyncThunk(
  "auth/forgotPasswordBackoffice",
  async (email: string, { rejectWithValue }) => {
    try {
      await axios.post(`${BACKEND_API_URL}/backoffice/auth/forgotPassword`, {
        email,
      });
      return { email };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send password reset email"
      );
    }
  }
);

// Send Verification Email - Backoffice
export const sendVerificationEmailBackoffice = createAsyncThunk(
  "auth/sendVerificationEmailBackoffice",
  async (user: BackofficeUserDTO, { rejectWithValue }) => {
    try {
      await axios.post(
        `${BACKEND_API_URL}/backoffice/auth/sendVerificationEmail`,
        { user }
      );
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send verification email"
      );
    }
  }
);

// Verify Email - Backoffice
export const verifyEmailBackoffice = createAsyncThunk(
  "auth/verifyEmailBackoffice",
  async (
    data: {
      syncCode: string;
      verificationCode: string;
      email: string;
      userId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        `${BACKEND_API_URL}/backoffice/auth/verifyEmail`,
        data
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to verify email"
      );
    }
  }
);

// Check User Email - Backoffice
export const checkUserEmailBackoffice = createAsyncThunk(
  "auth/checkUserEmailBackoffice",
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BACKEND_API_URL}/backoffice/auth/checkUserEmail/${encodeURIComponent(email)}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Email check failed"
      );
    }
  }
);

export const { clearError, syncToken, logout } = authSlice.actions;

// Mapping table: Web survey IDs to Backend Selectable Identifiers
// This maps the numeric IDs from web/src/constants/surveyQuestions.ts
// to the string identifiers used in MongoDB selectables collection
const CULTURE_KEY_MAPPING: Record<number, string> = {
  // Organisation Structure (Question 1)
  101: "collobarationandsupport",
  102: "responsibilityandrisk",
  103: "competenceandsuccess",
  104: "proceduralandclear",

  // Organisation Definition (Question 2)
  201: "teamwork",
  202: "innovation",
  203: "success",
  204: "sustainability",

  // Organisation Bonds (Question 3)
  301: "loyalty",
  302: "innovation",
  303: "success",
  304: "order",

  // Organisation Values (Question 4)
  401: "contribution",
  402: "progress",
  403: "competence",
  404: "sustainability",

  // Success Factors (Question 5)
  501: "teamwork",
  502: "uniqueproduct",
  503: "marketshare",
  504: "productivity",

  // Leadership Perspective (Question 6)
  601: "mentorship",
  602: "entrepreneurship",
  603: "resultoriented",
  604: "productivity",

  // Team Work (Question 7)
  701: "individualwork",
  702: "teamwork",
  703: "fun",
  704: "feedback",
  705: "motivation",

  // Career Opportunities (Question 8)
  801: "leadershipeducation",
  802: "materialreward",
  803: "promotion",
  804: "spiritualreward",

  // Priorities (Question 9)
  901: "culture",
  902: "teamwork",
  903: "promotion",
  904: "individualprogress",
  905: "careerdevelopment",
  906: "worklifebalance",
  907: "physicalconditions",
  908: "workhours",
  909: "worksatisfaction",
};

// Helper: Map survey responses to backend culture format
export const mapSurveyResponsesToCulture = (responses: any) => {
  // Helper to safely map items
  const safeMap = (arr: any[], mapper: (item: any, index: number) => any) => {
    if (!Array.isArray(arr)) return [];
    return arr.filter(Boolean).map(mapper);
  };

  return {
    organisationStructure: safeMap(responses[1] || [], (item: any) => ({
      key: CULTURE_KEY_MAPPING[item?.id as number] || item?.id?.toString() || "",
      percentage: item?.value || 0,
    })),
    organisationDefinition: safeMap(responses[2] || [], (item: any) => ({
      key: CULTURE_KEY_MAPPING[item?.id as number] || item?.id?.toString() || "",
      percentage: item?.value || 0,
    })),
    organisationBonds: safeMap(responses[3] || [], (item: any) => ({
      key: CULTURE_KEY_MAPPING[item?.id as number] || item?.id?.toString() || "",
      percentage: item?.value || 0,
    })),
    organisationValues: safeMap(responses[4] || [], (item: any) => ({
      key: CULTURE_KEY_MAPPING[item?.id as number] || item?.id?.toString() || "",
      percentage: item?.value || 0,
    })),
    successFactors: safeMap(responses[5] || [], (item: any) => ({
      key: CULTURE_KEY_MAPPING[item?.id as number] || item?.id?.toString() || "",
      percentage: item?.value || 0,
    })),
    leadershipPerspective: safeMap(responses[6] || [], (item: any) => ({
      key: CULTURE_KEY_MAPPING[item?.id as number] || item?.id?.toString() || "",
      percentage: item?.value || 0,
    })),
    teamWork: safeMap(responses[7] || [], (item: any) => ({
      key: CULTURE_KEY_MAPPING[item?.id as number] || item?.id?.toString() || "",
      percentage: item?.value || 0,
    })),
    careerOpportunities: safeMap(responses[8] || [], (id: any) =>
      CULTURE_KEY_MAPPING[id as number] || id?.toString() || ""
    ),
    priorities: safeMap(responses[9] || [], (item: any, index: number) => ({
      key: CULTURE_KEY_MAPPING[item?.id as number] || item?.id?.toString() || "",
      order: index + 1,
    })),
  };
};
export default authSlice.reducer;
